import { useMemo } from 'react'
import {
  type HotkeyCallback,
  type Keys,
  useHotkeys as useReactHotkeysHook,
} from 'react-hotkeys-hook'
import type {
  OptionsOrDependencyArray,
  RefType,
} from 'react-hotkeys-hook/dist/types.js'
import { create } from 'zustand'
import { combine, devtools } from 'zustand/middleware'
import { Hotkeys } from '#src/constants/hotkeys.ts'

export const useHotkeysStore = create(
  devtools(
    combine({ disabled: false }, (set) => ({
      setDisabled: (
        nextDisabled: boolean | ((currentDisabled: boolean) => boolean)
      ) => {
        set((state) => ({
          disabled:
            typeof nextDisabled === 'function'
              ? nextDisabled(state.disabled)
              : nextDisabled,
        }))
      },
    }))
  )
)

interface DisabledOverride {
  enabled?: boolean
}

export function useHotkeys<T extends HTMLElement>(
  keys: Keys,
  callback: HotkeyCallback,
  options?: OptionsOrDependencyArray,
  dependencies?: OptionsOrDependencyArray
): (instance: RefType<T>) => void {
  const disabled = useHotkeysStore((state) => state.disabled)
  const optionsWithDisabled = useMemo(() => {
    const singleHotkeys = new Set(
      Object.values(Hotkeys).filter((key) => !key.includes('+'))
    )
    const baseOptions = options instanceof Array ? undefined : options
    const disabledOverride: DisabledOverride = {}
    if (disabled) {
      disabledOverride.enabled = false
    }
    return {
      ...baseOptions,
      ...disabledOverride,
      ignoreEventWhen: (e: KeyboardEvent) => {
        if (
          singleHotkeys.has(e.key as Hotkeys) &&
          (e.ctrlKey || e.altKey || e.metaKey || e.shiftKey)
        ) {
          return true
        }
        return false
      },
    }
  }, [options, disabled])
  return useReactHotkeysHook(keys, callback, optionsWithDisabled, dependencies)
}
